import { useCallback, useMemo, useState } from 'react';

/**
 * Uživatelem přidaný řádek v sekci dokumentu (popisek + hodnota).
 * Vykresluje ho `DocCustomRows`, přidává `DocAddRowButton`.
 */
export interface DocCustomRow {
  id: string;
  k: string;
  v: string;
}

/** Serializovaný stav pro `data_json.radky` (archiv i lokální koncept). */
export interface DocRowsSnapshot {
  /** Klíče skrytých standardních řádků, např. `s01.povoleni`. */
  hidden: string[];
  /** Vlastní řádky po sekcích, např. `{ s01: [{ id, k, v }] }`. */
  custom: Record<string, DocCustomRow[]>;
}

export interface DocRows {
  // — skrývání standardních řádků —
  isHidden: (key: string) => boolean;
  hide: (key: string) => void;
  restoreSection: (section: string) => void;
  hiddenCount: (section: string) => number;
  // — vlastní řádky —
  customRows: (section: string) => DocCustomRow[];
  addCustomRow: (section: string) => void;
  updateCustomRow: (section: string, id: string, patch: Partial<DocCustomRow>) => void;
  removeCustomRow: (section: string, id: string) => void;
  /** Id naposledy přidaného řádku — `DocCustomRows` na něj přesune fokus. */
  lastAddedId: string | null;
  /** Pro uložení do `data_json.radky` / konceptu. */
  snapshot: () => DocRowsSnapshot;
  /** Obnova z `data_json.radky` (archiv, koncept). Neplatný vstup se ignoruje. */
  apply: (raw: unknown) => void;
  /** Vymaže skryté i vlastní řádky (nový dokument). */
  reset: () => void;
}

function newId(): string {
  return `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

const NO_ROWS: DocCustomRow[] = [];

/**
 * Stav řádků v hlavičkových sekcích dokumentu: skrytí standardního řádku (✕),
 * obnovení celé sekce a vlastní řádky „popisek: hodnota".
 *
 * Nahrazuje kopii `hidden/isHidden/hide/restoreSection/hiddenCount`, která byla
 * v každém formuláři zvlášť, a přidává vlastní řádky, které dřív nešly nikde.
 *
 * Klíč řádku je `<sekce>.<pole>` (`s01.cislo`); sekce se používá pro
 * `hiddenCount` / `restoreSection` i pro vlastní řádky.
 */
export function useDocRows(): DocRows {
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [custom, setCustom] = useState<Record<string, DocCustomRow[]>>({});
  const [lastAddedId, setLastAddedId] = useState<string | null>(null);

  const isHidden = useCallback((key: string) => hidden.has(key), [hidden]);
  const hide = useCallback((key: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);
  const restoreSection = useCallback((section: string) => {
    setHidden((prev) => {
      const next = new Set<string>();
      prev.forEach((k) => {
        if (!k.startsWith(section + '.')) next.add(k);
      });
      return next;
    });
  }, []);
  const hiddenCount = useCallback(
    (section: string) => {
      let n = 0;
      hidden.forEach((k) => {
        if (k.startsWith(section + '.')) n++;
      });
      return n;
    },
    [hidden],
  );

  const customRows = useCallback((section: string) => custom[section] ?? NO_ROWS, [custom]);
  const addCustomRow = useCallback((section: string) => {
    const row: DocCustomRow = { id: newId(), k: '', v: '' };
    setCustom((prev) => ({ ...prev, [section]: [...(prev[section] ?? []), row] }));
    setLastAddedId(row.id);
  }, []);
  const updateCustomRow = useCallback(
    (section: string, id: string, patch: Partial<DocCustomRow>) => {
      setCustom((prev) => ({
        ...prev,
        [section]: (prev[section] ?? []).map((r) => (r.id === id ? { ...r, ...patch } : r)),
      }));
    },
    [],
  );
  const removeCustomRow = useCallback((section: string, id: string) => {
    setCustom((prev) => {
      const list = (prev[section] ?? []).filter((r) => r.id !== id);
      const next = { ...prev };
      if (list.length) next[section] = list;
      else delete next[section];
      return next;
    });
  }, []);

  const snapshot = useCallback(
    (): DocRowsSnapshot => ({ hidden: Array.from(hidden), custom }),
    [hidden, custom],
  );

  const apply = useCallback((raw: unknown) => {
    if (!raw || typeof raw !== 'object') return;
    const d = raw as Partial<DocRowsSnapshot>;
    if (Array.isArray(d.hidden)) {
      setHidden(new Set(d.hidden.filter((k): k is string => typeof k === 'string')));
    }
    if (d.custom && typeof d.custom === 'object') {
      const out: Record<string, DocCustomRow[]> = {};
      for (const [section, rows] of Object.entries(d.custom)) {
        if (!Array.isArray(rows)) continue;
        const clean = (rows as unknown[])
          .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
          .map((r) => ({
            id: typeof r.id === 'string' && r.id ? r.id : newId(),
            k: typeof r.k === 'string' ? r.k : '',
            v: typeof r.v === 'string' ? r.v : '',
          }));
        if (clean.length) out[section] = clean;
      }
      setCustom(out);
    }
    setLastAddedId(null);
  }, []);

  const reset = useCallback(() => {
    setHidden(new Set());
    setCustom({});
    setLastAddedId(null);
  }, []);

  return useMemo(
    () => ({
      isHidden,
      hide,
      restoreSection,
      hiddenCount,
      customRows,
      addCustomRow,
      updateCustomRow,
      removeCustomRow,
      lastAddedId,
      snapshot,
      apply,
      reset,
    }),
    [
      isHidden,
      hide,
      restoreSection,
      hiddenCount,
      customRows,
      addCustomRow,
      updateCustomRow,
      removeCustomRow,
      lastAddedId,
      snapshot,
      apply,
      reset,
    ],
  );
}
